import React, { FC, useEffect, useId, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Store } from '../../../../redux';
import { Texts } from '../../../../shared/types';
import { botImage } from '../../../../assets';
import { useHttpMutation } from '../../../../api';
import { API_URLS } from '../../consts';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAlert } from '../../../../shared/components';
import { roomErrorMessage } from '@/screens/guest/lib/room-errors';
import { Minus, Plus } from 'lucide-react';
import { CREATE_ROOM_ROUTE } from '../../create-room/route.definitions';
import '../../components/social.css';

export declare type InviteBotsInput = {
  numberOfBots: number;
};

type InviteBotsText = Texts<'subText' | 'submit' | 'warning'>;
export interface InviteBotsProps {
  texts?: InviteBotsText;
}

const defaultText: InviteBotsText = {
  subText:
    'Omega fills empty seats so the game can start. Pick how many bots to seat; real players can still join until the table is full.',
  submit: 'Seat bots',
  warning: 'Create or join a table first, then seat bots in the empty chairs.',
};

/**
 * Seats the table can still give to bots. `numberOfPlayers` 0 means the
 * table size is unknown (legacy behaviour: allow one).
 */
export const botSeatsAvailable = (
  numberOfPlayers: number,
  seatedPlayers: number
) => (numberOfPlayers > 0 ? numberOfPlayers - (seatedPlayers || 1) : 1);

export const clampBots = (value: number, max: number) =>
  Math.max(max > 0 ? 1 : 0, Math.min(Math.max(max, 0), Math.round(value)));

export const InviteBots: FC<InviteBotsProps> = ({ texts = defaultText }) => {
  const {
    gameInfo: { room },
    players,
    isInRoom,
  } = useSelector((state: Store) => ({
    ...state.inPlayReducer,
    isInRoom: !!state.userReducer.isInRoom,
  }));

  const [params] = useSearchParams();
  const playersInParams = params.get('noOfPlayers');

  const { mutate, isLoading } = useHttpMutation<unknown, InviteBotsInput>(
    API_URLS.INVITE_BOTS,
    'POST'
  );

  const numberOfPlayers = useMemo(() => {
    if (playersInParams) {
      const parsed = parseInt(playersInParams, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return room?.numberOfPlayers || 0;
  }, [playersInParams, room?.numberOfPlayers]);

  const maxBotsAllowed = useMemo(
    () => botSeatsAvailable(numberOfPlayers, players.length),
    [players.length, numberOfPlayers]
  );

  const [botCount, setBotCount] = useState(() => clampBots(1, maxBotsAllowed));
  const [error, setError] = useState<string | null>(null);
  // the table may fill while this screen is open
  useEffect(() => {
    setBotCount(c => clampBots(c, maxBotsAllowed));
  }, [maxBotsAllowed]);

  const { addAlert } = useAlert();
  const nav = useNavigate();
  const valueId = useId();
  const hintId = useId();

  const tableFull = isInRoom && maxBotsAllowed <= 0;
  const canSubmit = isInRoom && !tableFull && !isLoading && botCount >= 1;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    mutate(
      { numberOfBots: Number(botCount) },
      {
        onError: err => {
          const message = roomErrorMessage(
            err,
            "Couldn't seat bots. Try again."
          );
          setError(message);
          addAlert({ message, type: 'error' });
        },
        onSuccess: () => {
          nav('/in-play');
        },
      }
    );
  };

  const seatsLabel = `${maxBotsAllowed} empty seat${maxBotsAllowed === 1 ? '' : 's'}`;

  return (
    <form className="bh-bots" onSubmit={onSubmit} noValidate>
      <div className="bh-bots__card">
        <div className="bh-bots__intro">
          <img src={botImage} alt="" className="bh-bots__avatar" />
          <div className="min-w-0">
            <h2 className="bh-bots__title">Meet Omega</h2>
            <p className="bh-bots__text">{texts.subText}</p>
          </div>
        </div>

        {!isInRoom ? (
          <div className="bh-banner" role="status">
            <span aria-hidden="true">♠</span>
            <span>
              {texts.warning}{' '}
              <Link to={CREATE_ROOM_ROUTE.ABSOLUTE_PATH} className="bh-link">
                Create a table
              </Link>
            </span>
          </div>
        ) : tableFull ? (
          <div className="bh-banner" role="status">
            <span aria-hidden="true">♣</span>
            <span>Every seat is taken, so there&apos;s no room for bots.</span>
          </div>
        ) : (
          <div className="bh-bots__picker">
            <p className="bh-bots__label" id={valueId}>
              Bots to seat
            </p>
            <div className="bh-stepper" role="group" aria-labelledby={valueId}>
              <button
                type="button"
                className="bh-btn bh-btn--secondary"
                onClick={() => setBotCount(c => clampBots(c - 1, maxBotsAllowed))}
                disabled={botCount <= 1 || isLoading}
                aria-label="One fewer bot"
              >
                <Minus size={20} aria-hidden="true" />
              </button>
              <output
                className="bh-bots__value"
                aria-live="polite"
                aria-describedby={hintId}
              >
                {botCount} {botCount === 1 ? 'bot' : 'bots'}
              </output>
              <button
                type="button"
                className="bh-btn bh-btn--secondary"
                onClick={() => setBotCount(c => clampBots(c + 1, maxBotsAllowed))}
                disabled={botCount >= maxBotsAllowed || isLoading}
                aria-label="One more bot"
              >
                <Plus size={20} aria-hidden="true" />
              </button>
            </div>
            <div className="bh-bots__seats" aria-hidden="true">
              {Array.from({ length: Math.min(maxBotsAllowed, 12) }).map((_, i) => (
                <span key={i} data-filled={i < botCount} />
              ))}
            </div>
            <p className="bh-small bh-muted" id={hintId}>
              {seatsLabel} at this table.
            </p>
          </div>
        )}

        {error && (
          <p className="bh-bots__error" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="bh-btn bh-btn--primary bh-btn--block"
          disabled={!canSubmit}
          aria-busy={isLoading}
        >
          {isLoading ? 'Seating bots…' : texts.submit}
        </button>
      </div>
    </form>
  );
};
